import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Code2,
  Layers,
  Cloud,
  Rocket,
  Server,
  Settings,
  ClipboardCheck,
  Workflow,
  Database,
  UploadCloud,
  BookOpen,
  FileCode,
  Network,
} from "lucide-react";

const AIXPlatform = () => {
   const [showMsg, setShowMsg] = useState(false);
  const appIcons = [
    "HRS", "WIOP", "HCMS", "GNO", "MZI", "RIF", "WCP", "WIS", "SLFT", "TCO",
    "TLM", "PMP", "WIOG", "DBG", "SIO", "PFE", "EORA/EORX", "FCML", "DFM", "FWI"
  ];   const itemsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-visible");
          }
        });
      },
      { threshold: 0.15 }
    );

    itemsRef.current.forEach((item) => {
      if (item) observer.observe(item);
    });

    return () => observer.disconnect();
  }, []);
  const features = [
    {
      icon: <Server className="w-8 h-8 text-emerald-400 mb-4" />,
      title: "Application Hosting",
      description: "Store and run Udbhav-built AI applications in a secure environment."
    },
    {
      icon: <FileCode className="w-8 h-8 text-green-400 mb-4" />,
      title: "Code Structure",
      description: "Follow standard folder and coding practice guidelines for consistency."
    },
    {
      icon: <Layers className="w-8 h-8 text-red-400 mb-4" />,
      title: "Reusable Modules",
      description: "Use existing components from earlier Udbhav projects to accelerate builds."
    },
    {
      icon: <Cloud className="w-8 h-8 text-emerald-400 mb-4" />,
      title: "Server Space",
      description: "Controlled hosting environment for development and deployment teams."
    },
    {
      icon: <Rocket className="w-8 h-8 text-green-400 mb-4" />,
      title: "Continuous Improvement",
      description: "Enhance, update, and evolve applications even after hackathons."
    },
    {
      icon: <Workflow className="w-8 h-8 text-red-400 mb-4" />,
      title: "Scalability Path",
      description: "Enable movement from prototype to full enterprise deployment."
    }
  ];

  const developerGuidelines = [
    "Code folder structure",
    "Integration steps",
    "API/Data use rules",
    "Deployment process",
    "Best practices"
  ];

  return (
    <div className="text-white bg-gradient-to-b from-black via-[#0b0b0b] to-[#0a0a0a] overflow-hidden">
{/* 🏁 1. HERO / INTRO SECTION */}
<section className="relative pt-32 pb-24 px-6 text-center overflow-hidden">
  {/* 🌈 Background glow */}
  <div className="absolute inset-0 -z-10">
    <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-r from-green-400/20 via-emerald-500/25 to-red-400/20 blur-[150px] animate-pulse rounded-full opacity-70" />
    <div className="absolute top-[45%] left-[10%] w-[350px] h-[350px] bg-green-500/10 blur-[90px] rounded-full" />
    <div className="absolute bottom-[10%] right-[10%] w-[250px] h-[250px] bg-red-400/10 blur-[90px] rounded-full" />
  </div>

  {/* 🌟 Main content */}
  <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center text-center space-y-6">
    
    {/* 🖼️ AIX Logo with glow and zoom */}
    <div className="relative mb-10 flex justify-center items-center overflow-hidden">
      {/* Ambient glow behind image */}
      <div className="absolute inset-0 flex justify-center items-center">
        <div className="w-[450px] h-[450px] bg-gradient-radial from-emerald-500/20 via-transparent to-transparent blur-[100px] animate-pulse-slow" />
      </div>

      {/* AIX image */}
      <img
        src="/images/aix.png"
        alt="AIX Platform"
        className="relative w-[360px] md:w-[260px] object-contain animate-zoom-flow select-none"
        style={{
          filter: "drop-shadow(0 0 25px rgba(16,185,129,0.5)) brightness(1.1)",
        }}
      />
    </div>

    {/* Animated Typing Title */}
    <h1
      className="relative text-2xl sm:text-3xl md:text-5xl font-extrabold 
                 bg-gradient-to-r from-green-400 via-emerald-300 to-red-400 
                 bg-clip-text text-transparent leading-tight overflow-hidden 
                 whitespace-normal sm:whitespace-nowrap border-r-4 border-emerald-400"
      style={{
        backgroundSize: "300% 100%",
        animation:
          "typing 4s steps(50, end), blink 0.75s step-end infinite 4s, textShimmer 6s linear infinite, removeBorder 0s 4s forwards",
      }}
    >
      Platform to Host & Scale Udbhav Solutions
    </h1>

    {/* Subtitle */}
    <p
      className="text-gray-300 text-base md:text-lg max-w-2xl leading-relaxed mt-10"
      style={{
        opacity: 0,
        animation: "fadeInUp 1s ease-out 0.3s forwards",
      }}
    >
      Empowering ONGC’s AI innovation journey with a secure, structured, and scalable platform.
    </p>

    {/* Highlights */}
    <div
      className="flex flex-wrap justify-center gap-10 mb-8"
      style={{
        opacity: 0,
        animation: "fadeInUp 1s ease-out 0.6s forwards",
      }}
    >
      {[
        "Applications built in Udbhav",
        "Structured development guidelines",
        "Dedicated server space",
      ].map((text, i) => (
        <div
          key={i}
          className="bg-white/10 px-5 py-2 rounded-full text-sm md:text-base font-medium border border-white/10 hover:bg-white/20 hover:scale-105 transition-all duration-300 backdrop-blur-md shadow-md shadow-emerald-500/10 mt-4"
        >
          {text}
        </div>
      ))}
    </div>
  </div>

  {/* Inline Keyframes */}
  <style>
    {`
      @keyframes typing {
        from { width: 0 }
        to { width: 100% }
      }
      @keyframes blink {
        from, to { border-color: transparent }
        50% { border-color: rgba(52, 211, 153, 0.8); }
      }
      @keyframes textShimmer {
        0% { background-position: 0% 50%; }
        100% { background-position: 200% 50%; }
      }
      @keyframes zoom-flow {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.08); }
      }
      @keyframes pulse-slow {
        0%, 100% { opacity: 0.5; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.1); }
      }
      @keyframes fadeInUp {
        0% { opacity: 0; transform: translateY(20px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      .animate-typing {
        display: inline-block;
        white-space: nowrap;
        overflow: hidden;
        border-right-width: 3px;
      }
    `}
  </style>
</section>




{/* 🧠 2. WHAT IS AIX */}
<section className="py-24 px-6 text-center bg-[#0a0a0a] relative overflow-hidden">
  {/* Subtle floating gradient light */}
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-green-400/10 via-emerald-400/10 to-red-400/10 blur-[120px] rounded-full opacity-70 animate-pulse -z-10"></div>

  <div className="max-w-4xl mx-auto">
    {/* Title with glow & shimmer */}
    <h2
      className="text-4xl md:text-5xl font-extrabold mb-8 bg-gradient-to-r from-red-400 via-green-400 to-emerald-400 bg-clip-text text-transparent tracking-wide"
      style={{
        backgroundSize: "300% 100%",
        animation: "textShimmer 10s linear infinite",
        textShadow: "0 0 18px rgba(16,185,129,0.3)",
      }}
    >
      What is AIX?
    </h2>

    {/* Accent underline */}
    <div
      className="mx-auto mb-10 rounded-full"
      style={{
        width: "100px",
        height: "4px",
        background: "linear-gradient(to right, #f87171, #34d399, #10b981)",
        boxShadow: "0 0 15px rgba(52, 211, 153, 0.5)",
        animation: "glowLine 3s ease-in-out infinite",
      }}
    ></div>

    {/* Content box */}
    <div
      className="px-6 py-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-lg shadow-emerald-500/10 hover:shadow-emerald-400/20 transition-transform duration-500 hover:scale-[1.02]"
      style={{
        opacity: 0,
        animation: "fadeInUp 1.2s ease-out forwards",
      }}
    >
      <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
        <span className="text-emerald-400 font-semibold">AIX</span> is the platform built under{" "}
        <span className="text-red-400 font-semibold">Udbhav</span> to continue the journey of
        innovation beyond the event.
        <br />
        <br />
        It provides a <span className="text-green-400 font-medium">common structure</span>,{" "}
        <span className="text-green-400 font-medium">server space</span>, and{" "}
        <span className="text-green-400 font-medium">deployment approach</span> so Udbhav
        applications remain active, improve over time, and can be deployed in real environments.
      </p>
    </div>
  </div>

  <style>
    {`
      @keyframes textShimmer {
        0% { background-position: 0% 50%; }
        100% { background-position: 100% 50%; }
      }
      @keyframes glowLine {
        0%, 100% { opacity: 0.8; transform: scaleX(1); }
        50% { opacity: 1; transform: scaleX(1.1); }
      }
      @keyframes fadeInUp {
        0% { opacity: 0; transform: translateY(25px); }
        100% { opacity: 1; transform: translateY(0); }
      }
    `}
  </style>
</section>
  
{/* 🚀 3. WHY AIX EXISTS */}
<section className="relative py-28 px-6 text-center bg-[#0a0a0a] overflow-hidden">
  {/* Soft glowing background accents */}
  <div className="absolute inset-0 -z-10">
    <div className="absolute top-[10%] left-[15%] w-[350px] h-[350px] bg-green-500/10 blur-[120px] rounded-full animate-pulse" />
    <div className="absolute bottom-[5%] right-[15%] w-[300px] h-[300px] bg-red-500/10 blur-[100px] rounded-full animate-[pulse-slow_6s_ease-in-out_infinite]" />
  </div>

  {/* Heading with subtle shimmer */}
  <h2
    className="text-4xl md:text-5xl font-extrabold mb-12 bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent tracking-wide"
    style={{
      backgroundSize: "300% 100%",
      animation: "textShimmer 10s linear infinite",
      textShadow: "0 0 20px rgba(16,185,129,0.25)",
    }}
  >
    Why AIX Exists
  </h2>

  {/* Animated underline accent */}
  <div
    className="mx-auto mb-16 rounded-full"
    style={{
      width: "110px",
      height: "4px",
      background: "linear-gradient(to right, #10b981, #34d399, #f87171)",
      boxShadow: "0 0 15px rgba(52, 211, 153, 0.5)",
      animation: "glowLine 3s ease-in-out infinite",
    }}
  ></div>

  {/* Card Grid */}
  <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-10 max-w-6xl mx-auto px-4">
    {[
      "Solutions should not stop at prototype stage",
      "Standard structure for all Udbhav AI projects",
      "Reuse modules from previous solutions",
      "Provide ongoing development & hosting environment",
      "Support long-term adoption inside the organisation",
      "Bridge between hackathons and enterprise use",
    ].map((text, i) => (
      <div
        key={i}
        className="group relative p-8 rounded-2xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] backdrop-blur-md transition-all duration-500 shadow-md hover:shadow-emerald-400/20 transform hover:-translate-y-2 hover:scale-[1.03]"
      >
        {/* Glow border effect */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-2xl bg-gradient-to-r from-green-400/10 via-emerald-400/10 to-red-400/10 blur-xl -z-10"></div>

        {/* Icon sparkle */}
        <div className="mb-5 flex justify-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-emerald-500 to-green-400 flex items-center justify-center text-black font-bold text-xl shadow-lg shadow-emerald-400/30 group-hover:scale-110 transition-transform duration-500">
            {i + 1}
          </div>
        </div>

        <p className="text-gray-200 text-lg leading-relaxed font-medium">{text}</p>
      </div>
    ))}
  </div>

  {/* Inline animations */}
  <style>
    {`
      @keyframes textShimmer {
        0% { background-position: 0% 50%; }
        100% { background-position: 100% 50%; }
      }
      @keyframes glowLine {
        0%, 100% { opacity: 0.8; transform: scaleX(1); }
        50% { opacity: 1; transform: scaleX(1.1); }
      }
      @keyframes pulse-slow {
        0%, 100% { opacity: 0.6; transform: scale(1); }
        50% { opacity: 0.8; transform: scale(1.05); }
      }
    `}
  </style>
</section>
{/* ⚙️ 4. WHAT AIX OFFERS */}
<section className="py-28 px-6 bg-black text-center relative overflow-hidden">
  {/* Decorative background glow */}
   <div className="absolute inset-0 -z-10">
    <div className="absolute top-[10%] left-[15%] w-[350px] h-[350px] bg-green-500/10 blur-[120px] rounded-full animate-pulse" />
    <div className="absolute bottom-[5%] right-[15%] w-[300px] h-[300px] bg-red-500/10 blur-[100px] rounded-full animate-pulse-slow" />
  </div>

  <h2 className="relative text-4xl md:text-5xl font-bold mb-14 bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(34,197,94,0.2)]">
    What AIX Offers
  </h2>

  <div className="relative grid sm:grid-cols-2 md:grid-cols-3 gap-10 max-w-6xl mx-auto z-10">
    {features.map((feature, index) => (
      <div
        key={index}
        className="group p-8 rounded-3xl bg-white/5 border border-white/10 transition-all duration-500 shadow-lg hover:shadow-emerald-400/10 hover:-translate-y-2 hover:bg-white/10 backdrop-blur-md"
      >
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="text-emerald-400 group-hover:scale-110 transition-transform duration-300">
            {feature.icon}
          </div>
          <h3 className="text-xl font-semibold text-white">{feature.title}</h3>
          <p className="text-gray-300 text-sm leading-relaxed">
            {feature.description}
          </p>
        </div>
      </div>
    ))}
  </div>

  {/* Subtle bottom gradient */}
  <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black to-transparent"></div>
</section>
    <section className="relative py-28 px-6 text-center bg-black overflow-hidden">
      {/* 🌌 Background glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.12),transparent_70%)] animate-pulse-slow"></div>

      <div className="max-w-4xl mx-auto relative z-10">
        <h2 className="text-4xl md:text-5xl font-bold mb-12 bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent drop-shadow-[0_0_12px_rgba(16,185,129,0.3)] animate-text-shimmer">
          Developer Guidelines
        </h2>

        {/* Animated Guidelines */}
        <ul className="text-gray-300 text-lg space-y-6 text-left sm:text-center sm:max-w-2xl mx-auto">
          {developerGuidelines.map((item, i) => (
            <li
              key={i}
              ref={(el) => (itemsRef.current[i] = el)}
              className={`flex items-center justify-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-5 py-3 backdrop-blur-md transition-all duration-700 hover:bg-white/10 hover:scale-[1.04] hover:shadow-[0_0_15px_rgba(16,185,129,0.3)] opacity-0 translate-y-6 ${
                i % 2 === 0 ? "slide-left" : "slide-right"
              }`}
            >
              <ClipboardCheck className="w-5 h-5 text-emerald-400 animate-icon-pop" />
              <span>{item}</span>
            </li>
          ))}
        </ul>


      </div>

      {/* 🌫 Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black to-transparent"></div>

      {/* Animations */}
      <style>
        {`
        @keyframes fadeUp {
          0% { opacity: 0; transform: translateY(25px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes icon-pop {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); }
        }
        @keyframes glowText {
          0%, 100% { text-shadow: 0 0 8px rgba(16,185,129,0.4); }
          50% { text-shadow: 0 0 20px rgba(16,185,129,0.8); }
        }
        @keyframes text-shimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
        .animate-text-shimmer {
          background-size: 300% 100%;
          animation: text-shimmer 6s linear infinite;
        }
        .animate-icon-pop {
          animation: icon-pop 0.6s ease;
        }

        /* Scroll-based slide animations */
        .slide-left {
          transform: translateX(-60px);
        }
        .slide-right {
          transform: translateX(60px);
        }
        .animate-visible {
          opacity: 1 !important;
          transform: translateX(0) translateY(0) !important;
          transition: all 0.8s ease-out;
        }
      `}
      </style>
    </section>
{/* 🌍 8. VISION AHEAD */}
<section className="relative py-28 px-6 text-center bg-black overflow-hidden">
  {/* Background glow + animated gradient ring */}
  <div className="absolute inset-0 -z-10">
    <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-gradient-to-r from-green-400/20 via-emerald-400/25 to-red-400/20 blur-[140px] rounded-full animate-pulse-slow"></div>
  </div>

  <div className="relative z-10 max-w-4xl mx-auto space-y-10">
    {/* Title with shimmer */}
    <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent animate-text-shimmer bg-[length:300%_100%]">
      Vision Ahead
    </h2>

    {/* Paragraph block */}
    <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto animate-fadeUpSlow">
      AIX will continue to grow with <span className="text-emerald-400 font-semibold">Udbhav 2.0</span> and future editions — 
      adding new applications and evolving into a long-term innovation platform.
    </p>

    {/* Highlight Card with subtle glow */}
    <div className="relative mt-12 bg-gradient-to-r from-white/5 to-transparent rounded-2xl border border-white/10 p-10 shadow-lg hover:shadow-emerald-500/20 transition-all duration-500 hover:-translate-y-1 backdrop-blur-md max-w-3xl mx-auto">
      <p className="text-2xl font-semibold text-gray-100 mb-8 leading-relaxed animate-glowText">
        “Udbhav creates solutions. AIX helps them live, scale, and create impact.”
      </p>
      {/* <Link to="/aix-applications">
        <Button className="bg-gradient-to-r from-green-500 via-emerald-400 to-red-500 text-white font-semibold text-lg rounded-full px-10 py-5 hover:scale-110 transition-all duration-500 shadow-lg shadow-emerald-500/30 relative overflow-hidden group">
          <span className="relative z-10">Explore AIX Applications</span>
          <span className="absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></span>
        </Button>
      </Link> */}
    </div>
  </div>

  {/* Decorative bottom fade */}
  <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black to-transparent"></div>

  {/* Inline keyframes */}
  <style>
    {`
      @keyframes pulse-slow {
        0%, 100% { opacity: 0.6; }
        50% { opacity: 1; }
      }
      .animate-pulse-slow {
        animation: pulse-slow 5s ease-in-out infinite;
      }

      @keyframes text-shimmer {
        0% { background-position: 0% 50%; }
        100% { background-position: 100% 50%; }
      }
      .animate-text-shimmer {
        animation: text-shimmer 6s linear infinite;
      }

      @keyframes fadeUpSlow {
        0% { opacity: 0; transform: translateY(25px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      .animate-fadeUpSlow {
        animation: fadeUpSlow 1s ease forwards;
      }

      @keyframes glowText {
        0%, 100% { text-shadow: 0 0 8px rgba(16,185,129,0.4); }
        50% { text-shadow: 0 0 20px rgba(16,185,129,0.8); }
      }
      .animate-glowText {
        animation: glowText 3s ease-in-out infinite;
      }
    `}
  </style>
</section>

    </div>
  );
};

export default AIXPlatform;
